import {
  TPlanBreakdownItem,
  TToolPaymentRules,
  TToolPlanAddOnsDetails,
  TToolPlanDetails,
} from "../types/tool-payments";
import { CURIO_HOST } from "./env";

export const OCT_FIRST = "2023-09-30T18:30:00.000Z";

export const NO_EXTEND_TRIAL_CUTOFF_ONBOARDING_DATE =
  "2023-11-30T18:30:00.000Z";

export const TRIAL_DAYS = 15;

// export const PLANS_WEBFLOW_URL = "https://eka-doc.webflow.io/";
export const PLANS_WEBFLOW_URL =
  "https://product.eka.care/practice-management/pricing";

export enum TOOL_PLANS {
  PLUS = "plus",
  PRO = "pro",
}

export enum TOOL_ADDONS {
  PHARMACY = "pharmacy",
  IPAD = "iPad",
}

export const proPlans = [
  TOOL_PLANS.PRO,
  TOOL_ADDONS.PHARMACY,
  TOOL_ADDONS.IPAD,
];

export const plusPlans = [TOOL_PLANS.PLUS];

export enum AxonPaymentPlan {
  APPOINTMENTS = "A",
  EMR = "E",
  EMR_PREMIUM = "P",
  INFRA = "I",
  SUPER = "X",
}

export const AxonPlanToPlanNameMap: Record<
  Exclude<AxonPaymentPlan, AxonPaymentPlan.SUPER>,
  TOOL_PLANS | TOOL_ADDONS
> = {
  A: TOOL_PLANS.PLUS,
  E: TOOL_PLANS.PRO,
  P: TOOL_ADDONS.PHARMACY,
  I: TOOL_ADDONS.IPAD,
};

export const ToolPlanBreakdown: Record<
  TOOL_PLANS,
  {
    planBrief: string;
    planIncludesPreText?: boolean;
    planBreakdown: Array<TPlanBreakdownItem>;
    url: string;
  }
> = {
  plus: {
    planBrief:
      "For doctors looking for a simple OPD management solution to boost their staff's productivity and their digital presence",
    planBreakdown: [
      {
        id: 1,
        name: "OPD management solution",
        infoBreakdown: [
          "Appointment Booking (0% commission)",
          "Billing Support",
          "Multi device support",
          "Staff Support and restrictions",
          "ABHA Compliance",
        ],
      },
      {
        id: 2,
        name: "Digital presence",
        infoBreakdown: [
          "Google Profile Management",
          "Proprietary Recommendation engine to improve Google Profile",
          "SEO Boost",
          "Support from 9AM to 9PM",
        ],
      },
    ],
    url: `${CURIO_HOST}/eka-doc-plans/plus-plan`,
  },
  pro: {
    planBrief:
      "For doctors looking for a complete clinic management solution with an EMR and new revenue opportunities",
    planIncludesPreText: true,
    planBreakdown: [
      {
        id: 1,
        name: "Personalised EMR",
        infoBreakdown: [
          "Digital Prescriptions",
          "WhatsApp prescription delivery",
          "Tele-consultations",
          "Migrations from existing EMR",
        ],
      },
      {
        id: 2,
        name: "Clinic Commerce",
        infoBreakdown: [
          "Earn by offering lab consultations at home to patients",
          "Earn by offering medicine delivery to patients",
          "Dedicated Relationship Manager",
        ],
      },
    ],
    url: `${CURIO_HOST}/eka-doc-plans/pro-plan`,
  },
};

/**
 * Addons Name Mapper - contains name of Addons to show
 */
export const AddonsMapper: Record<TOOL_ADDONS, string> = {
  pharmacy: "Own Pharmacy Integration",
  iPad: "iPad 9th Gen",
};

export const plusPlan: TToolPlanDetails = {
  planName: TOOL_PLANS.PLUS,
  axonPlanName: AxonPaymentPlan.APPOINTMENTS,
  amount: 4999,
  // discount: 100,
  discount: 0,
  eka_discount: 0,
  gst_amount: 0,
  payment_completed: false,
  persuation_text: "",
};

export const proPlan = {
  planName: TOOL_PLANS.PRO,
  axonPlanName: AxonPaymentPlan.EMR,
  amount: 9999,
  discount: 0,
  eka_discount: 0,
  gst_amount: 0,
  payment_completed: false,
  persuation_text: "",
  showAddOns: true,
};

export const PLANS: Array<TToolPlanDetails> = [plusPlan, proPlan];

// TODO: Capitalise/Rename later during Cleanup
export const ADD_ONS: Array<TToolPlanAddOnsDetails> = [
  {
    addOnName: TOOL_ADDONS.PHARMACY,
    price: 7000,
    paymentFrequency: "Per year",
    image: "https://cdn.eka.care/vagus/clmyvu8f500540thu86wc28pk.webp",
  },
  {
    addOnName: TOOL_ADDONS.IPAD,
    price: 28000,
    paymentFrequency: "One-time payment",
    image: "https://cdn.eka.care/vagus/clmyvs7te00050tku1bd4gsq4.webp",
  },
];

export const ToolAddonsBreakdown: Record<
  TOOL_ADDONS,
  { addonBreakdown: Array<TPlanBreakdownItem> }
> = {
  pharmacy: {
    addonBreakdown: [
      {
        id: 1,
        name: "",
        infoBreakdown: [
          "Real time inventory view while prescribing",
          "Real time pricing",
          "Seamless integration",
        ],
      },
    ],
  },
  iPad: {
    addonBreakdown: [
      {
        id: 1,
        name: "",
        infoBreakdown: ["Exclusive iPad EMR"],
      },
    ],
  },
};

// TODO: Trying something

/**
 * Trial Status divided on 5 day intervals till day 15
 */
export enum TRIAL_STATUS {
  EXISTING_MORE_THAN_1_YEAR = "existing_more_than_1_year",
  EXISTING_LESS_THAN_1_YEAR = "existing_less_than_1_year",
  MORE_THAN_15_DAYS = "more_than_15_days",
  LESS_THAN_15_DAYS = "less_than_15_days",
  LESS_THAN_10_DAYS = "less_than_10_days",
  LESS_THAN_5_DAYS = "less_than_5_days",
  OVER = "trail_over",
}

export const enum TRIAL_PERSUASION_PLANS {
  NEW_USER_BLUE_START = "new_user_blue_start",
  NEW_USER_BLUE_MID = "new_user_blue_mid",
  NEW_USER_YELLOW = "new_user_yellow",
  NEW_USER_RED = "new_user_red",
  NEW_USER_RED_ENDED = "new_user_red_ended",
  EXISTING_USER_BLUE_GRACE_OVER = "existing_user_blue_grace_over",
  EXISTING_USER_BLUE_GRACE_LEFT = "existing_user_blue_grace_left",
  EXISTING_USER_BLUE_START = "existing_user_blue_start",
  EXISTING_USER_BLUE_MID = "existing_user_blue_mid",
  EXISTING_USER_YELLOW = "existing_user_yellow",
  EXISTING_USER_RED = "existing_user_red",
  EXISTING_USER_RED_ENDED = "existing_user_red_ended",
  ACTIVE_PLAN = "active_plan",
}

export const ToolPaymentRules: TToolPaymentRules = {
  [TRIAL_PERSUASION_PLANS.NEW_USER_BLUE_START]: {
    no_of_days_start: 15,
    no_of_days_end: 15,
    offer_percentage: 20,
    offer_time_left: 48,
  },
  [TRIAL_PERSUASION_PLANS.NEW_USER_BLUE_MID]: {
    no_of_days_start: 15,
    no_of_days_end: 5,
    offer_percentage: 30,
    offer_time_left: 48,
  },
  [TRIAL_PERSUASION_PLANS.NEW_USER_YELLOW]: {
    no_of_days_start: 5,
    no_of_days_end: 3,
    offer_percentage: 30,
    offer_time_left: 48,
  },
  [TRIAL_PERSUASION_PLANS.NEW_USER_RED]: {
    no_of_days_start: 3,
    no_of_days_end: 0,
    offer_percentage: 30,
    offer_time_left: 48,
  },
  [TRIAL_PERSUASION_PLANS.NEW_USER_RED_ENDED]: {
    no_of_days_start: 0,
    no_of_days_end: -Infinity,
    offer_percentage: 30,
    offer_time_left: 48,
  },
  [TRIAL_PERSUASION_PLANS.EXISTING_USER_BLUE_GRACE_LEFT]: {
    no_of_days_start: 365,
    no_of_days_end: 15,
    offer_percentage: 30,
    offer_time_left: 48,
  },
  [TRIAL_PERSUASION_PLANS.EXISTING_USER_BLUE_GRACE_OVER]: {
    no_of_days_start: Infinity,
    no_of_days_end: 365,
    offer_percentage: 30,
    offer_time_left: 48,
  },
  [TRIAL_PERSUASION_PLANS.EXISTING_USER_BLUE_START]: {
    no_of_days_start: Infinity,
    no_of_days_end: 15,
    offer_percentage: 30,
    offer_time_left: 48,
  },
  [TRIAL_PERSUASION_PLANS.EXISTING_USER_BLUE_MID]: {
    no_of_days_start: 15,
    no_of_days_end: 5,
    offer_percentage: 30,
    offer_time_left: 48,
  },
  [TRIAL_PERSUASION_PLANS.EXISTING_USER_YELLOW]: {
    no_of_days_start: 5,
    no_of_days_end: 3,
    offer_percentage: 30,
    offer_time_left: 48,
  },
  [TRIAL_PERSUASION_PLANS.EXISTING_USER_RED]: {
    no_of_days_start: 3,
    no_of_days_end: 0,
    offer_percentage: 30,
    offer_time_left: 48,
  },
  [TRIAL_PERSUASION_PLANS.EXISTING_USER_RED_ENDED]: {
    no_of_days_start: 0,
    no_of_days_end: -Infinity,
    offer_percentage: 30,
    offer_time_left: 48,
  },
};

const GO_TO_PLAN_CART_TEXT = "Select plan";
const WILL_BUY_LATER = "I'll buy later";

export const FooterActions: Record<
  Exclude<TRIAL_PERSUASION_PLANS, TRIAL_PERSUASION_PLANS.ACTIVE_PLAN>,
  {
    secondaryButtonText?: string;
    primaryButtonText: string;
  }
> = {
  [TRIAL_PERSUASION_PLANS.NEW_USER_BLUE_START]: {
    secondaryButtonText: "Try free for 15 days",
    primaryButtonText: GO_TO_PLAN_CART_TEXT,
  },
  [TRIAL_PERSUASION_PLANS.NEW_USER_BLUE_MID]: {
    secondaryButtonText: WILL_BUY_LATER,
    primaryButtonText: GO_TO_PLAN_CART_TEXT,
  },
  [TRIAL_PERSUASION_PLANS.NEW_USER_YELLOW]: {
    primaryButtonText: "Schedule a demo",
  },
  [TRIAL_PERSUASION_PLANS.NEW_USER_RED]: {
    primaryButtonText: "Contact sales",
  },
  [TRIAL_PERSUASION_PLANS.NEW_USER_RED_ENDED]: {
    secondaryButtonText: "Extend trial (1 day)",
    // secondaryButtonText: "Contact Sales",
    primaryButtonText: GO_TO_PLAN_CART_TEXT,
  },
  [TRIAL_PERSUASION_PLANS.EXISTING_USER_BLUE_GRACE_LEFT]: {
    secondaryButtonText: "Contact sales",
    primaryButtonText: "I'll buy later",
  },
  [TRIAL_PERSUASION_PLANS.EXISTING_USER_BLUE_GRACE_OVER]: {
    secondaryButtonText: "Contact sales",
    primaryButtonText: "Extend my trial (3 months)",
  },
  [TRIAL_PERSUASION_PLANS.EXISTING_USER_BLUE_START]: {
    secondaryButtonText: "Extend trial (15 Days)",
    primaryButtonText: GO_TO_PLAN_CART_TEXT,
  },
  [TRIAL_PERSUASION_PLANS.EXISTING_USER_BLUE_MID]: {
    secondaryButtonText: WILL_BUY_LATER,
    primaryButtonText: GO_TO_PLAN_CART_TEXT,
  },
  [TRIAL_PERSUASION_PLANS.EXISTING_USER_YELLOW]: {
    primaryButtonText: "Schedule a demo",
  },
  [TRIAL_PERSUASION_PLANS.EXISTING_USER_RED]: {
    primaryButtonText: "Contact sales",
  },
  [TRIAL_PERSUASION_PLANS.EXISTING_USER_RED_ENDED]: {
    secondaryButtonText: "Extend trial (1 day)",
    // secondaryButtonText: "Contact Sales",
    primaryButtonText: GO_TO_PLAN_CART_TEXT,
  },
};
